// Screening decisions with undo (invariant 8). A decision is local to MeeMail: it reaches the
// server at once (screening out also sets the sender's open mail aside in Hidden) and Undo puts
// the previous decision back, restoring anything that decision set aside. Gmail is never touched.
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { SenderView } from "@shared/api";
import { feedApi } from "@web/lib/feedApi";
import { feedKeys, invalidateBoards } from "@web/lib/feedQueries";
import { sendersApi, type SenderListParams, type SenderScreen } from "@web/lib/sendersApi";
import { invalidateSenders, senderKeys } from "@web/lib/sendersQueries";
import { registerUndo } from "@web/lib/undoRegistry";
import type { Delivery, Destination } from "@shared/routing";
import {
  decisionMessage,
  destinationMessage,
  patchSenderList,
  senderName,
  setAsideByDecision,
  withDestination,
  withScreen,
} from "./screener";

/** Put a changed sender into every cached list and the single-sender cache; returns a rollback. */
export function patchSenderLocally(queryClient: QueryClient, changed: SenderView): () => void {
  const lists = queryClient.getQueriesData<SenderView[]>({ queryKey: senderKeys.lists });
  const singleKey = senderKeys.sender(changed.accountId, changed.address);
  const single = queryClient.getQueryData<SenderView | null>(singleKey);
  for (const [key, list] of lists) {
    if (!list) continue;
    const params = (key[1] ?? {}) as SenderListParams;
    queryClient.setQueryData(key, patchSenderList(list, params, changed));
  }
  queryClient.setQueryData(singleKey, changed);
  return () => {
    for (const [key, list] of lists) queryClient.setQueryData(key, list);
    queryClient.setQueryData(singleKey, single);
  };
}

function afterSetAside(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: feedKeys.hidden });
  invalidateBoards(queryClient);
}

/** Restore what one screen-out decision set aside (best effort; the rest stays in Hidden). */
async function restoreSetAside(queryClient: QueryClient, sender: SenderView, decidedAt: string) {
  const hidden = await feedApi.hidden({ limit: 100 });
  const rows = setAsideByDecision(hidden, sender, decidedAt);
  await Promise.all(rows.map((row) => feedApi.restoreHidden(row.id).catch(() => null)));
  if (rows.length) afterSetAside(queryClient);
}

/**
 * Allow, screen out, or return a sender to undecided. The row changes at once; if the server
 * refuses, it goes back and a toast says nothing changed.
 */
export async function screenSender(
  queryClient: QueryClient,
  sender: SenderView,
  screen: SenderScreen,
): Promise<boolean> {
  const previous = sender.screen;
  if (previous === screen) return true;
  const rollback = patchSenderLocally(
    queryClient,
    withScreen(sender, screen, new Date().toISOString()),
  );
  try {
    const next = await sendersApi.screen(sender.accountId, sender.address, screen);
    patchSenderLocally(queryClient, next);
    invalidateSenders(queryClient);
    if (screen === "screened_out") afterSetAside(queryClient);
    const decidedAt = next.screenedAt ?? new Date().toISOString();
    registerUndo({
      message: decisionMessage(next, screen),
      undo: async () => {
        const back = await sendersApi.screen(sender.accountId, sender.address, previous);
        patchSenderLocally(queryClient, back);
        invalidateSenders(queryClient);
        if (screen === "screened_out") await restoreSetAside(queryClient, next, decidedAt);
      },
      doneMessage:
        screen === "screened_out"
          ? `Undone. ${senderName(next)}’s mail is back where it was.`
          : `Undone. ${senderName(next)} is back to ${previous === "none" ? "undecided" : "how it was"}.`,
    });
    return true;
  } catch {
    rollback();
    toast("Couldn’t save that decision just now. Nothing changed.");
    return false;
  }
}

/** Put a sender back exactly as it was before a destination choice (Undo). */
async function restoreRouting(sender: SenderView): Promise<SenderView> {
  if (sender.screen === "none") return sendersApi.screen(sender.accountId, sender.address, "none");
  const destination =
    sender.screen === "screened_out" ? "screen_out" : (sender.destination ?? null);
  return sendersApi.destination(
    sender.accountId,
    sender.address,
    destination,
    sender.delivery ?? null,
  );
}

/**
 * U8: Cards, Brief only, Receipts (screen out keeps its own path, with what it set aside put back
 * on Undo). The row changes at once; Undo puts the previous choice back. Gmail is never touched.
 */
export async function routeSender(
  queryClient: QueryClient,
  sender: SenderView,
  destination: Destination,
  opts: { delivery?: Delivery | null; defaultLabel: string },
): Promise<boolean> {
  if (destination === "screen_out") return screenSender(queryClient, sender, "screened_out");
  const at = new Date().toISOString();
  const rollback = patchSenderLocally(
    queryClient,
    withDestination(sender, destination, opts.delivery, at),
  );
  try {
    const next = await sendersApi.destination(
      sender.accountId,
      sender.address,
      destination,
      opts.delivery,
    );
    patchSenderLocally(queryClient, next);
    invalidateSenders(queryClient);
    if (sender.screen === "screened_out") afterSetAside(queryClient);
    registerUndo({
      message: destinationMessage(next, destination, next.delivery, opts.defaultLabel),
      undo: async () => {
        patchSenderLocally(queryClient, await restoreRouting(sender));
        invalidateSenders(queryClient);
      },
      doneMessage: `Undone. ${senderName(next)} is back to how it was.`,
    });
    return true;
  } catch {
    rollback();
    toast("Couldn’t save that choice just now. Nothing changed.");
    return false;
  }
}
